import {
  Alert,
  Box,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useMemo, useState } from 'react';
import { useTranslator } from 'translation/useTranslator';
import { ITranslator } from 'translation/generated/translationInterface';
import { Button, TextField } from '@nps.today/component-library';
import { CustomAutocomplete } from 'components/util/CustomAutocomplete';
import { SelectItem } from 'components/util/select/selectData';
import useCustomDispatch from 'redux/dispatch';
import { RootState, useSelector } from 'redux/store';
import { createOrderType, updateOrderType } from 'redux/slices/orderTypes';
import {
  isGlobalClaimValue,
  moduleRows,
  resolveLabel,
  standaloneList
} from 'pages/organizations/components/permissions.helpers';
import SidePanel from 'components/dialog/SidePanel';
import { OrderType, OrderTypeCustomFieldDataType } from '../../../../@types/orderTypes';
import OrderTypeCustomFieldsTable from './OrderTypeCustomFieldsTable';
import {
  emptyOrderTypeDraft,
  OrderTypeDraft,
  OrderTypeDraftError,
  OrderTypeFieldDraft,
  toCreateOrderTypeRequest,
  toOrderTypeDraft,
  toUpdateOrderTypeRequest,
  validateCreateDraft,
  validateEditDraft
} from './orderTypeDraft.utils';

/**
 * The claim codes an order type may unlock, labeled like the user-permissions matrix: the
 * tenant-assignable set, as "<module>: <operation>" for CRUD claims and the standalone
 * permission label otherwise.
 */
const buildClaimOptions = (translator: ITranslator): SelectItem[] => {
  const opLabels: Record<string, string> = {
    c: translator.create(),
    r: translator.view(),
    u: translator.edit(),
    d: translator.delete()
  };

  const options = new Map<string, SelectItem>();
  for (const module of moduleRows) {
    for (const [op, claimValue] of module.opsMap) {
      if (isGlobalClaimValue(claimValue) || options.has(claimValue)) {
        continue;
      }
      const moduleLabel = resolveLabel(translator, module.translationKey, module.fallbackLabel);
      options.set(claimValue, {
        value: claimValue,
        displayValue: `${moduleLabel}: ${opLabels[op] ?? op}`
      });
    }
  }

  for (const permission of standaloneList) {
    if (isGlobalClaimValue(permission.value) || options.has(permission.value)) {
      continue;
    }
    options.set(permission.value, {
      value: permission.value,
      displayValue: resolveLabel(translator, permission.translationKey, permission.fallbackLabel)
    });
  }

  return Array.from(options.values());
};

let fallbackDraftIdCounter = 0;

const newFieldDraftId = (): string =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `field-${++fallbackDraftIdCounter}`;

const emptyFieldDraft = (): OrderTypeFieldDraft => ({
  draftId: newFieldDraftId(),
  key: '',
  label: '',
  dataType: OrderTypeCustomFieldDataType.String,
  isRequired: false,
  defaultValue: '',
  enumValues: []
});

const draftErrorMessage = (translator: ITranslator, error: OrderTypeDraftError): string => {
  switch (error.code) {
    case 'keyRequired':
      return translator.orderTypeKeyRequired();
    case 'keyInvalid':
      return translator.orderTypeKeyInvalid();
    case 'keyTooLong':
      return translator.orderTypeKeyTooLong();
    case 'keyInUse':
      return translator.orderTypeKeyInUse();
    case 'nameRequired':
      return translator.orderTypeNameRequired();
    case 'nameTooLong':
      return translator.orderTypeNameTooLong();
    case 'unitNameRequired':
      return translator.orderTypeUnitNameRequired();
    case 'unitNameTooLong':
      return translator.orderTypeUnitNameTooLong();
    case 'fieldKeyRequired':
      return translator.customFieldKeyRequired();
    case 'fieldKeyTooLong':
      return translator.customFieldKeyTooLong(error.fieldKey ?? '');
    case 'fieldKeysMustBeUnique':
      return translator.customFieldKeysMustBeUnique();
    case 'fieldLabelTooLong':
      return translator.customFieldLabelTooLong(error.fieldKey ?? '');
    case 'fieldDefaultValueTooLong':
      return translator.customFieldDefaultValueTooLong(error.fieldKey ?? '');
    case 'fieldEnumValuesRequired':
      return translator.customFieldEnumValuesRequired(error.fieldKey ?? '');
    case 'fieldEnumValuesMustBeUnique':
      return translator.customFieldEnumValuesMustBeUnique(error.fieldKey ?? '');
    case 'fieldEnumValuesTooLong':
      return translator.customFieldEnumValuesTooLong(error.fieldKey ?? '');
    case 'fieldDefaultValueInvalid':
      return translator.customFieldDefaultValueInvalid(error.fieldKey ?? '');
  }
};

type OrderTypeDrawerProps = {
  open: boolean;
  orderType: OrderType | null;
  onClose: () => void;
};

const OrderTypeDrawer = ({ open, orderType, onClose }: OrderTypeDrawerProps) => {
  const translator = useTranslator();
  const customDispatch = useCustomDispatch();
  const isEditing = orderType !== null;
  const orderTypes = useSelector((state: RootState) => state.orderTypes.orderTypes);
  const saving = useSelector((state: RootState) => state.orderTypes.loading.save === true);
  const [draft, setDraft] = useState<OrderTypeDraft>(emptyOrderTypeDraft());
  const [error, setError] = useState<OrderTypeDraftError | null>(null);
  const claimOptions = useMemo(() => buildClaimOptions(translator), [translator]);
  // Claims outside the pickable set (e.g. set through the API) still need to render and survive a save.
  const selectedClaimOptions = draft.claims.map(
    (claimValue) =>
      claimOptions.find((option) => option.value === claimValue) ?? {
        value: claimValue,
        displayValue: claimValue
      }
  );

  useEffect(() => {
    if (open) {
      setDraft(orderType ? toOrderTypeDraft(orderType) : emptyOrderTypeDraft());
      setError(null);
    }
  }, [open, orderType]);

  const patchDraft = (patch: Partial<OrderTypeDraft>) => {
    setDraft((previous) => ({ ...previous, ...patch }));
  };

  const patchField = (draftId: string, patch: Partial<OrderTypeFieldDraft>) => {
    setDraft((previous) => ({
      ...previous,
      fields: previous.fields.map((field) =>
        field.draftId === draftId ? { ...field, ...patch } : field
      )
    }));
  };

  const removeField = (draftId: string) => {
    setDraft((previous) => ({
      ...previous,
      fields: previous.fields.filter((field) => field.draftId !== draftId)
    }));
  };

  const save = () => {
    const validationError = isEditing
      ? validateEditDraft(draft)
      : validateCreateDraft(
          draft,
          orderTypes.map((existing) => existing.key)
        );
    setError(validationError);
    if (validationError) {
      return;
    }

    if (isEditing) {
      customDispatch({
        action: updateOrderType,
        actionParameters: { id: orderType.id, settings: toUpdateOrderTypeRequest(draft) },
        onSuccess: onClose
      });
    } else {
      customDispatch({
        action: createOrderType,
        actionParameters: toCreateOrderTypeRequest(draft),
        onSuccess: onClose
      });
    }
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      dataTestId="order-type-drawer"
      title={isEditing ? translator.editOrderType() : translator.addOrderType()}
      subtitle={
        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
          {isEditing ? orderType.id : translator.orderTypeIdAssignedOnSave()}
        </Typography>
      }
      footer={
        <>
          <Button dataTestId="order-type-drawer-cancel" onClick={onClose} disabled={saving}>
            {translator.cancel()}
          </Button>
          <Button
            dataTestId="order-type-drawer-save"
            variant="contained"
            loading={saving}
            onClick={save}
          >
            {isEditing ? translator.saveChanges() : translator.createOrderType()}
          </Button>
        </>
      }
    >
      <Stack gap={3}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2
          }}
        >
          <TextField
            dataTestId="order-type-drawer-key"
            label={translator.key()}
            value={draft.key}
            disabled={isEditing || saving}
            helperText={isEditing ? translator.orderTypeKeyCannotBeChanged() : undefined}
            placeholder="surveys"
            slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
            onChange={(event) => patchDraft({ key: event.target.value })}
          />
          <TextField
            dataTestId="order-type-drawer-name"
            label={translator.name()}
            value={draft.name}
            disabled={saving}
            placeholder="Surveys"
            onChange={(event) => patchDraft({ name: event.target.value })}
          />
          <TextField
            dataTestId="order-type-drawer-unit-name"
            label={translator.unitName()}
            value={draft.unitName}
            disabled={saving}
            placeholder="sendout"
            onChange={(event) => patchDraft({ unitName: event.target.value })}
          />
          <Stack justifyContent="flex-end">
            <FormControlLabel
              control={
                <Switch
                  data-testid="order-type-drawer-metered"
                  checked={draft.isMetered}
                  disabled={isEditing || saving}
                  onChange={(event) => patchDraft({ isMetered: event.target.checked })}
                />
              }
              label={translator.isMetered()}
            />
            <FormControlLabel
              control={
                <Switch
                  data-testid="order-type-drawer-active"
                  checked={draft.isActive}
                  disabled={saving}
                  onChange={(event) => patchDraft({ isActive: event.target.checked })}
                />
              }
              label={translator.isActive()}
            />
          </Stack>
        </Box>

        {error && <Alert severity="error">{draftErrorMessage(translator, error)}</Alert>}

        <Divider />

        <Stack gap={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Stack gap={0.25}>
              <Typography variant="subtitle1">{translator.customFieldDefinitions()}</Typography>
              <Typography variant="caption" color="text.secondary">
                {translator.customFieldDefinitionsDescription()}
              </Typography>
            </Stack>
            {!isEditing && (
              <Button
                dataTestId="order-type-drawer-add-field"
                startIcon={<AddIcon />}
                onClick={() =>
                  setDraft((previous) => ({
                    ...previous,
                    fields: [...previous.fields, emptyFieldDraft()]
                  }))
                }
              >
                {translator.addField()}
              </Button>
            )}
          </Stack>

          {isEditing ? (
            <OrderTypeCustomFieldsTable customFields={orderType.customFields} />
          ) : (
            <>
              {draft.fields.map((field) => (
                <Stack
                  key={field.draftId}
                  gap={2}
                  sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}
                >
                  <Stack direction="row" gap={1.5} alignItems="flex-start">
                    <TextField
                      dataTestId="order-type-drawer-field-key"
                      label={translator.key()}
                      value={field.key}
                      disabled={saving}
                      placeholder="channel"
                      slotProps={{ input: { sx: { fontFamily: 'monospace' } } }}
                      onChange={(event) => patchField(field.draftId, { key: event.target.value })}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      dataTestId="order-type-drawer-field-data-type"
                      select
                      label={translator.dataType()}
                      value={field.dataType}
                      disabled={saving}
                      onChange={(event) =>
                        patchField(field.draftId, {
                          dataType: event.target.value as OrderTypeCustomFieldDataType,
                          enumValues: [],
                          defaultValue: ''
                        })
                      }
                      sx={{ width: 140 }}
                    >
                      {Object.values(OrderTypeCustomFieldDataType).map((dataType) => (
                        <MenuItem key={dataType} value={dataType}>
                          {dataType}
                        </MenuItem>
                      ))}
                    </TextField>
                    <IconButton
                      data-testid="order-type-drawer-field-remove"
                      onClick={() => removeField(field.draftId)}
                      disabled={saving}
                      sx={{ mt: 1 }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Stack direction="row" gap={1.5}>
                    <TextField
                      dataTestId="order-type-drawer-field-label"
                      label={translator.label()}
                      value={field.label}
                      disabled={saving}
                      onChange={(event) => patchField(field.draftId, { label: event.target.value })}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      dataTestId="order-type-drawer-field-default-value"
                      label={translator.defaultValue()}
                      value={field.defaultValue}
                      disabled={saving}
                      onChange={(event) =>
                        patchField(field.draftId, { defaultValue: event.target.value })
                      }
                      sx={{ flex: 1 }}
                    />
                  </Stack>
                  {field.dataType === OrderTypeCustomFieldDataType.Enum && (
                    <CustomAutocomplete
                      dataTestId="order-type-drawer-field-enum-values"
                      multiple
                      freeSolo
                      options={[]}
                      value={field.enumValues}
                      disabled={saving}
                      onChange={(_, enumValues) =>
                        patchField(field.draftId, { enumValues: enumValues as string[] })
                      }
                      renderInput={(params) => (
                        <TextField
                          dataTestId="order-type-drawer-field-enum-values"
                          {...params}
                          label={translator.enumValues()}
                          helperText={translator.enumValuesDescription()}
                        />
                      )}
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        data-testid="order-type-drawer-field-required"
                        checked={field.isRequired}
                        disabled={saving}
                        onChange={(event) =>
                          patchField(field.draftId, { isRequired: event.target.checked })
                        }
                      />
                    }
                    label={translator.required()}
                  />
                </Stack>
              ))}
              {draft.fields.length === 0 && (
                <Box
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderStyle: 'dashed',
                    borderRadius: 1,
                    p: 3,
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {translator.noCustomFieldsYet()}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Stack>

        <Divider />

        <Stack gap={2}>
          <Stack gap={0.25}>
            <Typography variant="subtitle1">{translator.claims()}</Typography>
            <Typography variant="caption" color="text.secondary">
              {translator.orderTypeClaimsDescription()}
            </Typography>
          </Stack>
          <CustomAutocomplete
            dataTestId="order-type-drawer-claims"
            multiple
            options={claimOptions.filter((option) => !draft.claims.includes(option.value))}
            value={selectedClaimOptions}
            disabled={saving}
            getOptionLabel={(option: SelectItem) => option?.displayValue}
            getOptionKey={(option: SelectItem) => option.value}
            onChange={(_, options) =>
              patchDraft({ claims: (options as SelectItem[]).map((option) => option.value) })
            }
            renderInput={(params) => (
              <TextField
                dataTestId="order-type-drawer-claims"
                {...params}
                label={translator.claims()}
              />
            )}
          />
        </Stack>
      </Stack>
    </SidePanel>
  );
};

export default OrderTypeDrawer;
